import React, { useState } from 'react';
import { skills, skillCategories } from '../data/portfolioData';
import './Skills.css';

// ── Two visual modes the user can toggle between ─────────────────────────────
// Mode A: ORBITAL  – radial solar-system layout (original idea, now fixed)
// Mode B: GRID     – card grid with animated fill bars, themed to match site
// ─────────────────────────────────────────────────────────────────────────────

const CAT_COLORS = {
  blockchain: { accent:'#627eea', glow:'rgba(98,126,234,0.3)' },
  backend:    { accent:'#68a063', glow:'rgba(104,160,99,0.3)' },
  ai:         { accent:'#E84393', glow:'rgba(232,67,147,0.3)' },
  frontend:   { accent:'#61dafb', glow:'rgba(97,218,251,0.3)' },
  tools:      { accent:'#ff9900', glow:'rgba(255,153,0,0.3)'  },
  all:        { accent:'#7C3AED', glow:'rgba(124,58,237,0.3)' },
};

// ── Shared filter tabs ────────────────────────────────────────────────────────
function FilterTabs({ active, onChange, mode, onModeChange }) {
  return (
    <div className="sk-controls">
      <div className="sk-tabs" role="tablist">
        {skillCategories.map(cat => (
          <button
            key={cat.id}
            role="tab"
            aria-selected={active === cat.id}
            className={`sk-tab${active === cat.id ? ' sk-tab--active' : ''}`}
            onClick={() => onChange(cat.id)}
            style={active === cat.id ? {
              '--tab-accent': CAT_COLORS[cat.id]?.accent,
              '--tab-glow':   CAT_COLORS[cat.id]?.glow,
            } : {}}
          >
            {cat.label}
          </button>
        ))}
      </div>

      <div className="sk-mode-toggle" title="Switch layout">
        <button
          className={`sk-mode-btn${mode === 'orbital' ? ' active' : ''}`}
          onClick={() => onModeChange('orbital')}
          aria-label="Orbital view"
        >
          ◎ Orbital
        </button>
        <button
          className={`sk-mode-btn${mode === 'grid' ? ' active' : ''}`}
          onClick={() => onModeChange('grid')}
          aria-label="Grid view"
        >
          ▦ Grid
        </button>
      </div>
    </div>
  );
}

// ── ORBITAL VIEW ─────────────────────────────────────────────────────────────
function OrbitalView({ activeFilter }) {
  const [tooltip, setTooltip] = useState({ visible:false, x:0, y:0, text:'' });
  const active = skillCategories.find(c => c.id === activeFilter);
  const colors = CAT_COLORS[activeFilter] || CAT_COLORS.all;

  const handleMove = (e, s) => setTooltip({ visible:true, x:e.clientX+14, y:e.clientY-34, text:`${s.label} — ${s.desc}` });
  const handleLeave = () => setTooltip(t => ({ ...t, visible:false }));

  return (
    <div className="orb-view">
      {/* Glow backdrop */}
      <div className="orb-glow" style={{ background:`radial-gradient(ellipse 60% 50% at 50% 50%, ${colors.glow}, transparent)` }} />

      <div className="orb-stage">
        {/* Rings */}
        <div className="orb-ring orb-ring--lg" />
        <div className="orb-ring orb-ring--md" />
        <div className="orb-ring orb-ring--sm" />

        {/* Stars */}
        {[...Array(10)].map((_,i) => (
          <div key={i} className="orb-star" style={{
            left:`${10+Math.sin(i*71)*40}%`,
            top: `${10+Math.cos(i*71)*40}%`,
            animationDelay:`${i*0.4}s`,
          }} />
        ))}

        {/* Center */}
        <div className="orb-center" style={{ '--orb-accent': colors.accent, '--orb-glow': colors.glow }}>
          <span className="orb-center-icon">{active?.icon}</span>
          <span className="orb-center-label">{active?.label}</span>
          <span className="orb-center-sub">{active?.centerSub}</span>
        </div>

        {/* Skill nodes */}
        {skills.map(skill => {
          const visible = activeFilter === 'all' || skill.cat === activeFilter;
          const highlighted = activeFilter !== 'all' && skill.cat === activeFilter;
          const rad = (skill.ang * Math.PI) / 180;
          const cx = 50 + (skill.r / 265) * 50 * Math.cos(rad - Math.PI / 2);
          const cy = 50 + (skill.r / 265) * 50 * Math.sin(rad - Math.PI / 2);

          return (
            <div
              key={skill.id}
              className={`orb-node${visible ? ' visible' : ' hidden'}${highlighted ? ' highlighted' : ''}`}
              style={{ left:`${cx}%`, top:`${cy}%`, '--node-accent': skill.bg }}
              onMouseMove={e => handleMove(e, skill)}
              onMouseLeave={handleLeave}
            >
              {/* Filled circle with logo */}
              <div
                className="orb-node-circle"
                style={{ background: skill.bg }}
              >
                <span className={`orb-logo orb-logo--${skill.logoType}`}>
                  {skill.logo}
                </span>
              </div>
              <span className="orb-node-label">{skill.label}</span>
            </div>
          );
        })}
      </div>

      {tooltip.visible && (
        <div className="orb-tooltip" style={{ left:tooltip.x, top:tooltip.y }}>
          {tooltip.text}
        </div>
      )}
    </div>
  );
}

// ── GRID VIEW ─────────────────────────────────────────────────────────────────
function GridView({ activeFilter }) {
  const filtered = activeFilter === 'all'
    ? skills
    : skills.filter(s => s.cat === activeFilter);

  return (
    <div className="grid-view">
      {filtered.map((skill, i) => (
        <div
          key={skill.id}
          className="grid-card"
          style={{
            '--card-accent': skill.bg,
            animationDelay: `${i * 0.04}s`,
          }}
        >
          <div className="grid-card-top">
            <div
              className="grid-card-icon"
              style={{ background: skill.bg }}
            >
              <span className={`orb-logo orb-logo--${skill.logoType} orb-logo--lg`}>
                {skill.logo}
              </span>
            </div>
            <div className="grid-card-info">
              <div className="grid-card-name">{skill.label}</div>
              <div className="grid-card-desc">{skill.desc}</div>
            </div>
          </div>
          <div className="grid-card-bar-wrap">
            <div className="grid-card-bar" />
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Main Skills component ─────────────────────────────────────────────────────
export default function Skills() {
  const [filter, setFilter] = useState('all');
  const [mode,   setMode]   = useState('orbital');

  return (
    <section id="skills" className="skills-section">
      <div className="skills-inner">
        <div className="section-label" style={{ textAlign:'center' }}>Skills</div>
        <h2 className="section-title" style={{ textAlign:'center' }}>
          What I <span className="sk-accent">Build With</span>
        </h2>
        <p className="section-sub" style={{ margin:'0 auto 2rem', textAlign:'center' }}>
          Select a category to explore the stack — switch between layouts with the toggle.
        </p>

        <FilterTabs
          active={filter}
          onChange={setFilter}
          mode={mode}
          onModeChange={setMode}
        />

        {mode === 'orbital'
          ? <OrbitalView activeFilter={filter} />
          : <GridView    activeFilter={filter} />
        }
      </div>
    </section>
  );
}
